/**
 * Pure logic for the one-timeline trip view.
 *
 * Flights are the backbone. Everything else the trip holds (hotel, events,
 * transfers, Steward-added stops) lives in the stretch BETWEEN flights that its
 * date falls in: before the first flight, the stay between two flights, and
 * after the last one. This module turns the backend's day spine into that shape
 * and nothing else; it renders nothing and fetches nothing.
 */
import type {
  FlightSegment,
  ItineraryDay,
  ItinerarySegment,
} from "@/lib/types";
import { dayHeading, isFlight } from "./itineraryDay-utils";

export type SectionKind = "before" | "stay" | "after";

export interface TripSection {
  /** Stable key, unique within one timeline. */
  id: string;
  kind: SectionKind;
  /** "Before you fly" / "In Las Vegas · Wed–Sun" / "Coming home". */
  title: string;
  /** Days that fall in this section, flights removed. May include free days. */
  days: ItineraryDay[];
  /** First and last date the section covers, when it has any. */
  startDate: string | null;
  endDate: string | null;
  /**
   * The date an "add" in this section should prefill: the first day it holds, or
   * for an empty section the date of the flight beside it.
   */
  anchorDate: string | null;
}

export type TimelineEntry =
  | { type: "section"; section: TripSection }
  | { type: "flight"; flight: FlightSegment };

export interface BuildOptions {
  /** Used for the title of a stay when the flight carries no destination city. */
  destinationFallback?: string | null;
}

const ISO = /^\d{4}-\d{2}-\d{2}$/;

function flightDate(f: FlightSegment, fallback: string): string {
  return f.departDate && ISO.test(f.departDate) ? f.departDate : fallback;
}

/** "Wed–Sun" for a range, "Wed" for one day, null for none. */
export function weekdayRange(start: string | null, end: string | null): string | null {
  if (!start) return null;
  const a = dayHeading(start).weekdayShort;
  if (!end || end === start) return a;
  return a + "–" + dayHeading(end).weekdayShort;
}

/**
 * Which flight does a point in the day belong before or after?
 * `time` null means "unknown": the later half (after landing) is the safer
 * default, because most undated-time rows on a flight day are check-ins and
 * dinners, not the airport transfer.
 */
function isBeforeFlight(time: string | null | undefined, flight: FlightSegment): boolean {
  if (!time || !flight.departLocal) return false;
  return time < flight.departLocal;
}

/**
 * Bucket days into the timeline.
 *
 * Rules:
 *  - Flights are taken from `days`, ordered by departure date (ties keep the
 *    backend's order). No flights: one section holding every day.
 *  - A day strictly before the first flight is "before"; strictly after the last
 *    is "after"; strictly between flights i and i+1 is the stay after flight i.
 *  - A day that carries a flight is split at the flight's departure time: rows
 *    that start earlier belong before it, the rest (and anything with no time)
 *    after it. Spans that merely continue (ongoing) travel with the later half.
 *  - A day whose date does not parse is kept (never dropped) in the first stay,
 *    or the only section.
 *  - One-way trips have no return: the stretch after the single flight is the
 *    stay at the destination, not "Coming home".
 */
export function buildTimeline(
  days: ItineraryDay[],
  opts: BuildOptions = {},
): TimelineEntry[] {
  const flights: FlightSegment[] = [];
  days.forEach((d) => {
    d.segments.forEach((s) => {
      if (isFlight(s)) flights.push(s);
    });
  });
  const dated = days.map((d) => d.date).filter((d) => ISO.test(d)).sort();
  const firstDay = dated[0] ?? "";
  flights.sort((a, b) =>
    flightDate(a, firstDay).localeCompare(flightDate(b, firstDay)),
  );

  const stripFlights = (segs: ItinerarySegment[]) =>
    segs.filter((s) => !isFlight(s));

  if (flights.length === 0) {
    return [
      {
        type: "section",
        section: makeSection("stay-0", "stay", "Your trip", days),
      },
    ];
  }

  // buckets[0] = before flight 0; buckets[i] = after flight i-1 (i = 1..n).
  const buckets: ItineraryDay[][] = Array.from(
    { length: flights.length + 1 },
    () => [],
  );
  const put = (idx: number, day: ItineraryDay) => {
    const list = buckets[idx];
    const at = list.findIndex((d) => d.date === day.date);
    if (at >= 0) {
      list[at] = {
        ...list[at],
        segments: [...list[at].segments, ...day.segments],
        ongoing: [...list[at].ongoing, ...day.ongoing],
        empty: list[at].empty && day.empty,
      };
    } else {
      list.push(day);
    }
  };
  const dates = flights.map((f) => flightDate(f, firstDay));

  for (const day of days) {
    if (!ISO.test(day.date)) {
      put(Math.min(1, flights.length), {
        ...day,
        segments: stripFlights(day.segments),
      });
      continue;
    }
    // Index of the first flight departing on or after this day.
    const idx = dates.findIndex((fd) => fd >= day.date);
    if (idx === -1) {
      put(flights.length, { ...day, segments: stripFlights(day.segments) });
      continue;
    }
    const sameDay = dates[idx] === day.date;
    if (!sameDay) {
      // Strictly before flight idx: it is the stretch BEFORE that flight.
      put(idx, { ...day, segments: stripFlights(day.segments) });
      continue;
    }
    // A flight day: split the rows at the departure time. Several flights on one
    // date split at the last of them, so only the earliest rows stay before.
    const flight = flights[idx];
    const before: ItinerarySegment[] = [];
    const after: ItinerarySegment[] = [];
    for (const seg of stripFlights(day.segments)) {
      const t = seg.kind === "item" ? seg.start_time : null;
      (isBeforeFlight(t, flight) ? before : after).push(seg);
    }
    if (before.length > 0) {
      put(idx, { ...day, segments: before, ongoing: [], empty: false });
    }
    if (after.length > 0 || day.ongoing.length > 0) {
      put(idx + 1, {
        ...day,
        segments: after,
        empty: after.length === 0 && day.ongoing.length === 0,
      });
    }
  }

  const out: TimelineEntry[] = [];
  const last = flights.length - 1;
  // Coming home: the last flight is the return leg, or it lands back where the
  // first one left. A one-way trip's tail is a stay at the destination.
  const comingHome =
    flights[last].legRole === "return" ||
    (flights.length > 1 && flights[last].destination === flights[0].origin);

  const cityOf = (f: FlightSegment) =>
    f.destinationCity || f.destination || opts.destinationFallback || null;

  buckets.forEach((bucket, i) => {
    if (i > 0) out.push({ type: "flight", flight: flights[i - 1] });
    const first = bucket[0]?.date ?? null;
    const lastDate = bucket[bucket.length - 1]?.date ?? null;
    // The stretch before the first flight and after the last always shows, even
    // empty: it is where "get to the airport" and "get home" get added. A stay
    // between two flights with nothing in it (back to back) adds only a header.
    if (bucket.length === 0 && i !== 0 && i !== flights.length) return;
    let kind: SectionKind;
    let title: string;
    if (i === 0) {
      kind = "before";
      title = "Before you fly";
    } else if (i === flights.length && comingHome) {
      kind = "after";
      title = "Coming home";
    } else {
      kind = "stay";
      const city = cityOf(flights[i - 1]);
      // The stay runs from landing to the next departure, even when the flight
      // days themselves hold no rows.
      const prev = flights[i - 1];
      const rangeStart =
        (prev.arriveDate && ISO.test(prev.arriveDate) ? prev.arriveDate : null) ??
        dates[i - 1] ??
        first;
      const rangeEnd = i < flights.length ? dates[i] : lastDate;
      const range = weekdayRange(rangeStart || first, rangeEnd);
      title = (city ? "In " + city : "Your stay") + (range ? " · " + range : "");
    }
    out.push({
      type: "section",
      section: makeSection(
        kind + "-" + i,
        kind,
        title,
        bucket,
        i === 0 ? dates[0] : (flights[i - 1].arriveDate ?? dates[i - 1]),
      ),
    });
  });

  return out;
}

function makeSection(
  id: string,
  kind: SectionKind,
  title: string,
  days: ItineraryDay[],
  neighbourDate: string | null = null,
): TripSection {
  const sorted = [...days].sort((a, b) => a.date.localeCompare(b.date));
  const valid = sorted.filter((d) => ISO.test(d.date));
  return {
    id,
    kind,
    title,
    days: sorted,
    startDate: valid[0]?.date ?? null,
    endDate: valid[valid.length - 1]?.date ?? null,
    anchorDate: valid[0]?.date ?? (neighbourDate && ISO.test(neighbourDate) ? neighbourDate : null),
  };
}

/**
 * Which section starts open: the one holding today, else the next one that
 * starts after today, else the last that has anything in it. Returns the id, or
 * null when there are no sections.
 */
export function defaultOpenSectionId(
  entries: TimelineEntry[],
  today: string,
): string | null {
  const sections = entries
    .filter((e): e is Extract<TimelineEntry, { type: "section" }> => e.type === "section")
    .map((e) => e.section);
  if (sections.length === 0) return null;
  const holding = sections.find(
    (s) => s.startDate && s.endDate && s.startDate <= today && today <= s.endDate,
  );
  if (holding) return holding.id;
  const next = sections.find((s) => s.startDate && s.startDate > today);
  if (next) return next.id;
  return sections[sections.length - 1].id;
}

/** The section whose date range contains `date`, for deep links by day. */
export function sectionIdForDate(
  entries: TimelineEntry[],
  date: string,
): string | null {
  for (const e of entries) {
    if (e.type !== "section") continue;
    if (e.section.days.some((d) => d.date === date)) return e.section.id;
  }
  return null;
}
